/**
 * Digital-mode project resolution.
 *
 * A digital route param (`/:projectId`) may be either:
 *   - a **slug** — the normal consumer URL, e.g. `dreambooth.app/prambanan-jazz`
 *   - a raw **Mongo ObjectId** — a superadmin QA convenience so an existing,
 *     slug-less project can be exercised via
 *     `app.dreamboothstudio.com/{id}?mode=digital` without first minting a slug.
 *
 * Both paths hit the PUBLIC, `isPublic`-gated studio endpoint
 * (`/api/projects/by-slug`, which accepts `slug=` or `id=`) and both require
 * `isPublic === true` — digital only ever serves shareable booths. The
 * `/api/projects/token` endpoint independently re-checks `isPublic` before
 * minting a session token, so this resolution is not a trust boundary on its
 * own.
 *
 * Returns the resolved Mongo `_id`, or `null` when the param doesn't resolve
 * to a public, active project. Re-throws `AbortError` so callers can
 * distinguish cancellation from a genuine miss.
 */

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i

export async function resolveDigitalProjectId(
  param: string,
  signal?: AbortSignal
): Promise<string | null> {
  const base = process.env.VITE_API_URL
  const query = OBJECT_ID_RE.test(param)
    ? `id=${encodeURIComponent(param)}`
    : `slug=${encodeURIComponent(param)}`

  try {
    const res = await fetch(`${base}/api/projects/by-slug?${query}`, {
      credentials: 'include',
      cache: 'no-cache',
      signal,
    })
    if (!res.ok) return null
    const project = (await res.json()) as { _id?: string; isPublic?: boolean }
    if (!project?._id) return null
    // Digital is for public booths only. by-slug gates the id path on
    // isPublic server-side; enforce here too for the slug path and defense.
    if (project.isPublic !== true) return null
    return project._id
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error
    return null
  }
}
